import React, { Suspense } from 'react';
import { compose } from 'redux';
import { VA_ON_HC_CONVERSATION_PATH, ScreenName, VA_ON_PORTAL_CONVERSATION_PATH } from '@helpcenter/constants';
import { isUnifiedHelpAddFilterForCustomerAccounts } from '@helpcenter/feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import type { RouteContext } from 'react-resource-router';
import { useQueryParam, usePathParam } from 'react-resource-router';
import { HelpCenterVirtualAgentResource } from 'unified-help-center-alpha/src/pages/conversation-page/resource';
import { withJsmAiConfigEnabled } from 'view/common/with-jsm-ai-config';
import withOnlyAtlassianAccount from 'view/common/with-only-atlassian-account';
import { withOnlyJiraAdmin } from 'view/common/with-only-jira-admin';
import { withOnlyLoggedIn } from 'view/common/with-only-logged-in';
import { withOnlyProjectAdmin } from 'view/common/with-only-project-admin';
import {
    HelpCenterConversationPage,
    PortalConversationPage,
    authPageMessage,
    callToActionUrlText,
    ErrorPage,
    Layout,
} from 'view/unified-help-center';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { getHelpCenterUrl } from '@atlassian/help-center-common-util/url';

const Error = () => <ErrorPage />;

const WrappedConversationPage = () => (
    <Layout
        renderError={Error}
        component={HelpCenterConversationPage}
        screenName={ScreenName.CONVERSATION_PAGE}
        headerless
        footerless
    />
);

export const ConversationHomePage = () => {
    const WrappedComponent = isUnifiedHelpAddFilterForCustomerAccounts()
        ? compose(withOnlyLoggedIn({ redirectUrl: '/' }))(() => <WrappedConversationPage />)
        : compose(
              withOnlyLoggedIn({ redirectUrl: '/' }),
              withOnlyAtlassianAccount()
          )(() => <WrappedConversationPage />);
    return <WrappedComponent />;
};

export const HelpCenterConversationPageRoute = () => {
    di(withJsmAiConfigEnabled, withOnlyJiraAdmin);
    const { formatMessage } = useIntl();

    const forbiddenPageOptions = {
        message: formatMessage(authPageMessage),
        callToActionText: formatMessage(callToActionUrlText),
        callToActionUrl: getHelpCenterUrl(),
    };

    const enhancer = compose(withOnlyLoggedIn({ redirectUrl: '/' }), withJsmAiConfigEnabled(forbiddenPageOptions));

    const WrappedComponent = enhancer(() => <WrappedConversationPage />);
    return <WrappedComponent />;
};

export const getHelpCenterConversationPageRoute = () => {
    const viewConversationPageRoute = {
        name: ScreenName.CONVERSATION_PAGE,
        path: VA_ON_HC_CONVERSATION_PATH,
        exact: true,
        resources: [HelpCenterVirtualAgentResource],
        component: ConversationHomePage,
    };

    return [
        {
            ...viewConversationPageRoute,
            component: HelpCenterConversationPageRoute,
        },
    ];
};

export const PortalConversationPageRoute = (props: RouteContext) => {
    di(withJsmAiConfigEnabled, withOnlyProjectAdmin);
    const { formatMessage } = useIntl();
    const [isTestConversationParam] = useQueryParam('isTestConversation');
    const [isVSAAdoptionConversation] = useQueryParam('vsaAdoption');
    const [portalIdAsString] = usePathParam('portalId');

    const forbiddenPageOptions = {
        message: formatMessage(authPageMessage),
        callToActionText: formatMessage(callToActionUrlText),
        callToActionUrl: portalIdAsString ? `/portal/${portalIdAsString}` : getHelpCenterUrl(),
        hideBanner:
            FeatureGates.checkGate('jsm_vsa_adoption_flickering_issue_fix') && isVSAAdoptionConversation === 'true',
    };

    const enhancer = compose(
        withOnlyLoggedIn({ redirectUrl: '/' }),
        ...(isTestConversationParam === 'true' ? [withOnlyProjectAdmin(forbiddenPageOptions)] : [])
    );

    const WrappedComponent = enhancer(() => (
        <Layout
            renderError={Error}
            component={PortalConversationPage}
            screenName={ScreenName.CONVERSATION_PAGE}
            headerless
            footerless
        />
    ));
    return (
        <Suspense fallback={null}>
            <WrappedComponent {...props} />
        </Suspense>
    );
};

export const viewPortalConversationPageRoute = {
    name: ScreenName.CONVERSATION_PAGE,
    path: VA_ON_PORTAL_CONVERSATION_PATH,
    exact: true,
    component: PortalConversationPageRoute,
};
